import React, { useRef, useState } from "react";
import { BsArrowLeft } from "react-icons/bs";
import { MdAdd, MdDelete, MdRefresh } from "react-icons/md";
import { TbFileUpload } from "react-icons/tb";
import { toast } from "react-hot-toast";
import useAuthAxios from "../../../utils/baseAxios";
import { API } from "../../../constants/api";
import { useMutation } from "@tanstack/react-query";
import { errorMessage } from "../../../utils/errorMessage";
import ClipLoader from "react-spinners/ClipLoader";
import { mergeAndAccumulate } from "../../../utils/mergeAndAccumulate";
import { normalizeReceiptType } from "../../../utils/giftcardReceiptType";
import axios from "axios";

type UploadStatus = "uploading" | "done" | "error";

interface UploadRecord {
  id: number;
  file: File;
  url: string | null;
  status: UploadStatus;
}

const UploadGiftcard = ({
  setOpenGicardUpload,
  setOpenGiftcardPreview,
  setOpenGicardDone,
  cardType,
  cardName,
  denominationData,
  cardId,
  receiptName,
}: any) => {
  const [uploads, setUploads] = useState<UploadRecord[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [ecode, setEcode] = useState<string>("");
  const axiosInstance = useAuthAxios();
  const nextId = useRef(0);

  const selectedUpload =
    uploads.find((upload) => upload.id === selectedId) ?? null;
  const disabled =
    uploads.length === 0 || uploads.some((upload) => upload.status !== "done");

  const updateUpload = (id: number, changes: Partial<UploadRecord>) => {
    setUploads((prev) =>
      prev.map((upload) =>
        upload.id === id ? { ...upload, ...changes } : upload
      )
    );
  };

  const uploadToBucket = async (id: number, file: File) => {
    updateUpload(id, { status: "uploading", url: null });
    try {
      const response = await axiosInstance.post(
        API.requestGiftCardImageUpload,
        {}
      );
      const { signedURL, objectURL } = response.data?.data ?? {};
      if (!signedURL || !objectURL) {
        throw new Error("Failed to get upload link");
      }
      await axios.put(signedURL, file, {
        headers: {
          "Content-Type": file.type,
        },
      });
      updateUpload(id, { status: "done", url: objectURL });
    } catch (e) {
      const err = e as any;
      updateUpload(id, { status: "error", url: null });
      toast.error(errorMessage(err?.data?.message || err?.message));
    }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files ? event.target.files[0] : null;
    event.target.value = "";
    if (!selectedFile) return;
    const id = nextId.current++;
    setUploads((prev) => [
      ...prev,
      { id, file: selectedFile, url: null, status: "uploading" },
    ]);
    setSelectedId(id);
    uploadToBucket(id, selectedFile);
  };

  const handleRemoveUpload = (id: number) => {
    setUploads((prev) => {
      const remaining = prev.filter((upload) => upload.id !== id);
      setSelectedId(remaining.length ? remaining[0].id : null);
      return remaining;
    });
  };

  const sendEcard = async ({
    cardId,
    countryName,
    tradeValue,
    code,
    type,
  }: any) => {
    const response = await axiosInstance.post(API.createGiftCardTransaction, {
      cardId,
      countryName,
      tradeValue,
      code,
      type,
    });
    return response.data;
  };
  const sendPcard = async ({
    cardId,
    countryName,
    tradeValue,
    images,
    type,
  }: any) => {
    const response = await axiosInstance.post(API.createGiftCardTransaction, {
      cardId,
      countryName,
      tradeValue,
      images,
      type,
    });
    return response.data;
  };

  const completeSendEcard = useMutation({
    mutationFn: sendEcard,
    onSuccess: (r) => {
      toast.success(r.message);
      setTimeout(() => {
        setOpenGicardUpload(false);
        setOpenGicardDone(true);
      }, 2000);
    },
    onError: (e) => {
      const err = e as any;
      toast.error(errorMessage(err?.message || err?.data?.message));
    },
  });
  const completeSendPcard = useMutation({
    mutationFn: sendPcard,
    onSuccess: (r) => {
      toast.success(r.message);
      setTimeout(() => {
        setOpenGicardUpload(false);
        setOpenGicardDone(true);
      }, 2000);
    },
    onError: (e) => {
      const err = e as any;
      toast.error(errorMessage(err?.message || err?.data?.message));
    },
  });

  const validateEcode = (ecodee: string) => {
    if (!ecodee) {
      toast.error("Please add the E-code");
      return false;
    }
    return true;
  };
  const handlesendEcard = () => {
    if (!validateEcode(ecode)) {
      return;
    }
    const data = {
      cardId: cardId,
      countryName: cardName,
      tradeValue: mergeAndAccumulate(denominationData),
      code: ecode,
      type: "ecode",
    };
    completeSendEcard.mutate(data);
  };
  const handlesendPcard = () => {
    const type = normalizeReceiptType(receiptName);

    const imageURLs = uploads
      .filter((upload) => upload.status === "done" && upload.url)
      .map((upload) => upload.url);
    if (imageURLs.length !== uploads.length) {
      toast.error("Some images are still uploading or failed to upload");
      return;
    }

    const data = {
      cardId: cardId,
      countryName: cardName,
      tradeValue: mergeAndAccumulate(denominationData),
      images: imageURLs,
      type: type,
    };
    completeSendPcard.mutate(data);
  };

  return (
    <div className="fixed inset-0  flex font-manrope justify-start items-start pt-16 overflow-auto pb-16 bg-[#E7EBEE] dark:bg-primary_dark dark:bg-opacity-95  backdrop-blur-sm">
      <div
        className={` w-11/12 mds:w-8/12 md:7/12 border dark:border-[#303030] bg-white  rounded-xl mx-auto p-6 dark:bg-[#192025]   lgss:w-2/5 xxl:w-1/3 `}
      >
        <div className="w-full flex justify-start gap-6 items-center">
          <button
            onClick={() => {
              setOpenGicardUpload(false);
              setOpenGiftcardPreview(true);
              setUploads([]);
              setSelectedId(null);
            }}
          >
            <BsArrowLeft className="text-black dark:text-white text-[20px]" />
          </button>
          <h4 className="text-gray-900  dark:text-gray-100 font-sora font-semibold text-[20px]">
            Upload card
          </h4>
        </div>
        {cardType === "ecode" ? (
          <div className="flex justify-center items-start flex-col">
            <div className="w-full mt-8">
              <h4 className="text-[13px] text-center lgss:text-[17px] text-gray-900 dark:text-gray-100">
                Separate the codes with comma (,) if you are uploading more than
                one card
              </h4>
              <input
                type="text"
                placeholder="Type Ecode here "
                value={ecode}
                onChange={(e) => setEcode(e.target.value)}
                className="border outline-none  border-gray-200  dark:placeholder:text-white h-[52px]  mt-8 rounded-xl px-4 w-full "
              />
            </div>
            <button
              disabled={!ecode || completeSendEcard.isPending}
              onClick={handlesendEcard}
              className={
                !ecode
                  ? "mt-24 bg-gray-400 flex justify-center items-center text-gray-200 text-[15px] font-medium h-[52px] w-full rounded-xl"
                  : "mt-10 bg-secondary_button flex justify-center items-center text-white text-[15px] font-medium h-[52px] w-full rounded-xl"
              }
            >
              {completeSendEcard.isPending ? (
                <ClipLoader color="#ffffff" size={20} />
              ) : (
                "Send and notify trader"
              )}
            </button>
          </div>
        ) : (
          <div className="w-full mt-8">
            <h4 className="text-gray-500 text-[12px] text-center">
              For a seamless giftcard transaction, kindly provide clear
              high-quality photos of the giftcard and its details, including the
              reciepts if available. Specify essential information like the card
              balance, expiration dateand issuer. Use the Deex's upload feature
              for secure submission and verify accuracy before finalizing.
            </h4>
            <div
              className={
                selectedUpload
                  ? "w-1/2 mds:w-2/5 h-[160px] rounded-xl  flex justify-center items-center mt-12 mx-auto"
                  : "w-1/2 mds:w-2/5 h-[160px] rounded-xl border-dashed flex justify-center items-center border border-gray-500 mt-12 mx-auto"
              }
            >
              {selectedUpload ? (
                <div className="w-full relative h-full flex flex-row-reverse">
                  <div className="bg-white dark:bg-gray-800 absolute p-1 top-1 right-2 flex justify-center items-center gap-2 rounded-[10px]">
                    {selectedUpload.status === "uploading" ? (
                      <ClipLoader color="#0B75C2" size={20} />
                    ) : (
                      <>
                        {selectedUpload.status === "error" && (
                          <MdRefresh
                            onClick={() =>
                              uploadToBucket(
                                selectedUpload.id,
                                selectedUpload.file
                              )
                            }
                            className="text-xl cursor-pointer text-secondary_button"
                          />
                        )}
                        <MdDelete
                          onClick={() => handleRemoveUpload(selectedUpload.id)}
                          className="text-xl cursor-pointer  text-red-600 "
                        />
                      </>
                    )}
                  </div>
                  {selectedUpload.status === "error" && (
                    <div className="absolute bottom-1 left-2 right-2 text-center text-[11px] font-medium text-white bg-red-600 bg-opacity-90 rounded-lg py-1">
                      Upload failed — retry or remove
                    </div>
                  )}

                  <img
                    src={URL.createObjectURL(selectedUpload.file)}
                    alt="uploaded giftcard"
                    className="h-full w-full bg-cover rounded-xl"
                  />
                </div>
              ) : (
                <div className="w-full h-full cursor-pointer flex justify-center items-center gap-2 flex-col">
                  <TbFileUpload className="text-[56px] text-gray-400 dark:text-gray-600" />
                  <div className="relative inline-block">
                    <label htmlFor="image-upload" className="cursor-pointer">
                      <button className="p-2 text-secondary_button rounded-xl bg-[#D4EBFD] text-[14px] font-medium">
                        Upload Card
                      </button>
                    </label>
                    <input
                      type="file"
                      id="image-upload"
                      onChange={handleImageUpload}
                      accept="image/*"
                      className="absolute w-24 left-1  opacity-0 "
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="w-full grid grid-cols-3 mds:grid-cols-4 mt-8 gap-5">
              {uploads.map((upload) => (
                <div
                  key={upload.id}
                  onClick={() => setSelectedId(upload.id)}
                  className={
                    upload.status === "error"
                      ? "h-[80px] rounded-lg flex relative border-2 border-red-600 justify-center items-center"
                      : "h-[80px] rounded-lg flex relative justify-center items-center"
                  }
                >
                  <img
                    src={URL.createObjectURL(upload.file)}
                    className="w-full h-full bg-cover rounded-lg"
                    alt=""
                  />
                  {upload.status === "uploading" && (
                    <div className="absolute inset-0 flex justify-center items-center bg-black bg-opacity-40 rounded-lg">
                      <ClipLoader color="#ffffff" size={20} />
                    </div>
                  )}
                </div>
              ))}
              <div className="h-[80px] rounded-lg flex border-dashed border border-secondary_button justify-center items-center ">
                <label htmlFor="image-upload-more" className="cursor-pointer">
                  <MdAdd className="text-[16px] text-secondary_button" />
                </label>
                <input
                  type="file"
                  id="image-upload-more"
                  onChange={handleImageUpload}
                  style={{ display: "none" }}
                  accept="image/*"
                  className="absolute w-40 top-4 left-3 opacity-0  h-20"
                />
              </div>
            </div>
            <button
              disabled={disabled || completeSendPcard.isPending}
              onClick={() => {
                handlesendPcard();
              }}
              className={
                disabled
                  ? "mt-10 bg-gray-400 flex justify-center items-center text-gray-200 text-[15px] font-medium h-[48px] w-full rounded-xl"
                  : "mt-10 bg-secondary_button flex justify-center items-center text-white text-[15px] font-medium h-[48px] w-full rounded-xl"
              }
            >
              {completeSendPcard.isPending ? (
                <ClipLoader color="#ffffff" size={20} />
              ) : (
                "Send and notify trader"
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadGiftcard;
